
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { addDoc, collection, getDoc, doc as docRef, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../Components/Firebase";
import { Button, Form, Container, Alert } from "react-bootstrap";

const Upload = () => {
  const [title, setTitle] = useState("");
  const [genre, setGenre] = useState("");
  const [file, setFile] = useState(null);
  const [nsfw, setNsfw] = useState(false);
  const [error, setError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      const duration = video.duration;
      if (duration > 600) {
        setError("Video exceeds 10-minute limit.");
        setFile(null);
      } else {
        setFile(selected);
        setError("");
      }
    };
    video.src = URL.createObjectURL(selected);
  };

  
const checkMultipleThumbnailsNSFW = async (videoId) => {
  const base = `https://videodelivery.net/${videoId}/thumbnails/thumbnail.jpg`;
  const times = ["1s", "30s", "60s"];
  const models = "nudity-2.1,gore-2.0,violence,genai";

  for (const time of times) {
    const url = `${base}?time=${time}`;
    const params = new URLSearchParams({
      url,
      models,
      api_user: process.env.REACT_APP_SIGHTENGINE_USER,
      api_secret: process.env.REACT_APP_SIGHTENGINE_SECRET
    });

    let isNSFW = false;
    let nsfwReason = "";

    try {
      const res = await fetch(`https://api.sightengine.com/1.0/check.json?${params.toString()}`);
      const data = await res.json();
      if (!res.ok) {
        console.error("Sightengine API error:", data);
        continue;
      }

      const unsafe =
        data.nudity?.raw > 0.6 ||
        data.gore?.prob > 0.6 ||
        data.violence?.prob > 0.6;

      
      if (unsafe) {
        console.warn(`Thumbnail at ${time} flagged NSFW`, data);
        return {
          nsfw: true,
          reason: `NSFW detected at ${time}`
        };
      }

    } catch (err) {
      console.error("NSFW check failed:", err);
    }
  }

  return { nsfw: false, reason: '' };
};

const handleUpload = async (e) => {
    e.preventDefault();
    setError("");

    if (!file) {
      return setError("Please select a video file.");
    }

    const user = auth.currentUser;
    if (!user) return setError("You must be logged in.");

    const userDoc = await getDoc(docRef(db, "users", user.uid));
    if (!userDoc.exists() || userDoc.data().role !== "creator") {
      return setError("Only creators can upload content.");
    }

    const accountId = process.env.REACT_APP_CLOUDFLARE_ACCOUNT_ID;
    const token = process.env.REACT_APP_CLOUDFLARE_TOKEN;

    setUploading(true);

    let isNSFW = false;
    let nsfwReason = "";

    try {
      const createUploadRes = await fetch(
        `https://api.cloudflare.com/client/v4/accounts/${accountId}/stream/direct_upload`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            maxDurationSeconds: 600,
            meta: { name: title || "Untitled" }
          })
        }
      );

      const createUploadData = await createUploadRes.json();
      if (!createUploadData.success) {
        console.error("Cloudflare upload URL error:", createUploadData.errors);
        throw new Error("Cloudflare did not provide an upload URL.");
      }

      const uploadURL = createUploadData.result.uploadURL;
      const videoId = createUploadData.result.uid;

      const formData = new FormData();
      formData.append("file", file);

      await fetch(uploadURL, { method: "POST", body: formData });

      await addDoc(collection(db, "episodes"), {
        nsfwReason,
        title,
        genre,
        nsfw,
        date: serverTimestamp(),
        creatorId: user.uid,
        videoId,
        trending: false,
        founderBadge: false
      });

      
      setUploadSuccess(true);
      if (isNSFW) {
        console.warn("Upload flagged as NSFW:", nsfwReason);
        alert("⚠️ Your upload was flagged as NSFW and will be hidden from Explore.");
      }

    } catch (err) {
      console.error(err);
      setError("Upload failed. Please try again.");
    }

    setUploading(false);
  };

  return (
    <div className="explore-page">
      <div className="star-field">
        {Array.from({ length: 100 }).map((_, i) => (
          <div
            key={i}
            className="star"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      <Container className="py-5 text-white">
        <div className="text-center mb-5">
          <h1 className="display-4 fw-bold">Upload an Episode</h1>
          <p className="text-light">Keep it under 10 minutes. Share your short-form anime with the world!</p>
        </div>

        {uploadSuccess && (
          <div className="text-center bg-success bg-opacity-25 border border-success p-4 rounded mb-4">
            <h4 className="text-success fw-bold">🎉 Upload Successful!</h4>
            <p className="text-light">Your episode is now live on your creator profile.</p>
            <div className="d-flex justify-content-center gap-3 mt-3">
              <Button variant="outline-light" onClick={() => navigate(`/creator/${auth.currentUser.uid}`)}>
                View My Profile
              </Button>
              <Button variant="success" onClick={() => window.location.reload()}>
                Upload Another
              </Button>
            </div>
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleUpload} className="bg-dark p-4 rounded shadow-lg">
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              value={title}
              required
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter episode title"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Genre</Form.Label>
            <Form.Control
              type="text"
              value={genre}
              required
              onChange={(e) => setGenre(e.target.value)}
              placeholder="e.g. Action, Fantasy, Mecha"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Upload Video</Form.Label>
            <Form.Control
              type="file"
              accept="video/*"
              onChange={handleFileChange}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="This episode contains NSFW content"
              checked={nsfw}
              onChange={() => setNsfw(!nsfw)}
            />
          </Form.Group>

          <Button type="submit" disabled={uploading} className="w-100 mt-3">
            {uploading ? "Uploading..." : "Upload Episode"}
          </Button>
        </Form>
      </Container>
    </div>
  );
};

export default Upload;